import { useEffect, useMemo, useState } from 'react';
import { MapContainer } from 'react-leaflet';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '@/data/models/trip-leg-status.model';
import { TripLegAddress } from '@/data/models/reservation.model';
import { TransportTripLeg } from '@/data/models/transport-reservation.model';
import { DriverLocation } from '@/data/models/driver-location.model';

import { useAppDispatch } from '@/hooks/useAppDispatch';

import MapWithNavigation from '../../home/LazyMapWithNavigation';
import { useDriverLocation } from '@/hooks/useDriverLocation';

const PREFIX = 'MemberTripLegLiveTracking';

const classes = {
  container: `${PREFIX}-container`,
  map: `${PREFIX}-map`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: 170,
    flex: 1,
    width: '100%',
  },
  [`& .${classes.map}`]: {
    minHeight: 233,
    height: '100%',
    width: '100%',
    '& div.leaflet-routing-alternatives-container': {
      display: 'none',
    },
  },
}));

/**
 * Validates if driver location data is valid and usable for display
 */
const isValidDriverLocation = (location: DriverLocation | null): boolean => {
  if (!location) return false;

  const lat = parseFloat(location.latitude);
  const lng = parseFloat(location.longitude);

  return !!(lat && lng && location.driverID);
};

/**
 * Checks if trip leg status should show live tracking with driver location
 */
const shouldShowLiveTracking = (tripLeg: TransportTripLeg): boolean => {
  return tripStatusWithEtaList.includes(
    tripLeg.tripLegStatusBC?.toLocaleLowerCase() as TripLegStatus,
  );
};

const MemberTripLegLiveTracking = ({
  tripLeg,
}: {
  tripLeg: TransportTripLeg;
}) => {
  const dispatch = useAppDispatch();

  const [tripEndPoints, setTripEndPoints] = useState<{
    pickupAddress: TripLegAddress;
    dropoffAddress: TripLegAddress;
  }>();
  const [center, setCenter] = useState({
    lat: 0,
    lng: 0,
  });

  const driverLocation = useDriverLocation({
    driverID: tripLeg?.driverID,
    tripLegID: tripLeg?.tripLegID,
  });

  const isLiveTrip = useMemo(() => {
    return shouldShowLiveTracking(tripLeg);
  }, [tripLeg.tripLegStatusBC]);

  useEffect(() => {
    if (tripLeg) {
      setCenter({
        lat: parseFloat(tripLeg.pickUpAddressLatitude) || 0,
        lng: parseFloat(tripLeg.pickUpAddressLongitude) || 0,
      });
      setTripEndPoints({
        pickupAddress: {
          addressLine1: tripLeg.pickUpAddressLine1,
          addressLine2: tripLeg.pickUpAddressLine2,
          city: tripLeg.pickUpCity,
          stateCode: tripLeg.pickUpStateCode,
          zipCode: tripLeg.pickUpZipCode,
          county: tripLeg.pickUpCounty,
          latitude: parseFloat(tripLeg.pickUpAddressLatitude),
          longitude: parseFloat(tripLeg.pickUpAddressLongitude),
        },
        dropoffAddress: {
          addressLine1: tripLeg.dropOffAddressLine1,
          addressLine2: tripLeg.dropOffAddressLine2,
          city: tripLeg.dropOffCity,
          stateCode: tripLeg.dropOffStateCode,
          zipCode: tripLeg.dropOffZipCode,
          county: tripLeg.dropOffCounty,
          latitude: parseFloat(tripLeg.dropOffAddressLatitude),
          longitude: parseFloat(tripLeg.dropOffAddressLongitude),
        },
      });
    }
  }, [dispatch, tripLeg]);

  return (
    <StyledBox className={classes.container}>
      <MapContainer
        className={classes.map}
        zoom={11}
        center={{
          lat: parseFloat(tripLeg.pickUpAddressLatitude) || 0,
          lng: parseFloat(tripLeg.pickUpAddressLongitude) || 0,
        }}>
        <MapWithNavigation
          tripLegs={tripEndPoints ? [tripEndPoints] : []}
          driverLocation={isLiveTrip ? driverLocation : null}
          fitBounds
        />
      </MapContainer>
    </StyledBox>
  );
};

export default MemberTripLegLiveTracking;
